import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { replace } from 'connected-react-router'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'
import HeadData from 'components/Common/HeadData'
import {
  deleteSearchParams, setManySearchParamValues, setSearchParamValue,
  parseSearchString,
  validateBoundsSearchParams,
  validateDestinationSearchParams,
  validateLandmarkSearchParams,
  validateNaturalLanguageSearchParams,
  validatePropertySearchParams,
} from 'lib/url/searchUrlUtils'
import type { SearchFormRef } from 'components/Search'
import config from 'constants/config'
import { fetchSpecificPlaces, fetchDestinationImageId } from 'actions/PlaceActions'
import { addGTMEvent } from 'api/googleTagManager'
import { searchOfferListFilters, searchResultDealClick, searchResultImpressionCount, searchSessionDealClick } from 'analytics/eventDefinitions'
import {
  createSearchSessionKey,
  decodeSearchParams,
  getSearchKey,
  getSearchTypeFromFilters,
} from 'lib/search/searchUtils'
import { shouldHideAnytimePrices } from 'lib/search/hideAnytimePrices'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import HotelSearchNoSearchResultSection from '../../HotelSearchPage/HotelSearchNoSearchResultSection' // TODO Move these into a common AccommodationSearchResults folder
import LayoutContainer from 'components/Common/LayoutContainer'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import {
  GlobalSearchStateActions,
  GLOBAL_SEARCH_INITIAL_STATE,
} from 'contexts/GlobalSearch/GlobalSearchState'
import { replaceWithRegion } from 'actions/NavigationActions'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import type { TrackingOptions } from 'hooks/Offers/useOfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import HotelSearchHeader from '../../HotelSearchPage/HotelSearchHeader' // TODO Move these into a common AccommodationSearchResults folder
import { landmarkSearchSortByTypes, homesAndVillasSortByTypes, SORT_OPTION_CUSTOMER_RATING } from 'constants/offerListFilters'
import useQueryParams from 'hooks/useQueryParams'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import { rem } from 'polished'
import clsx from 'clsx'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  ANYWHERE_NAME,
  ANYWHERE_PLACE_ID,
} from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { FilterControlChangeHandler } from '../../HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from '../../HotelSearchPage/HotelSearchPageFilterPane' // TODO Move these into a common AccommodationSearchResults folder
import OfferCountAndSort from '../../HotelSearchPage/OfferCountAndSort' // TODO Move these into a common AccommodationSearchResults folder
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import { getListName } from 'analytics/enhanced-ecommerce'
import type { EnabledFilters } from 'components/Search/type'
import { useGlobalHomesAndVillasSearch } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHomesAndVillasSearch'
import {
  HOMES_AND_VILLAS_FILTERS,
  HOMES_AND_VILLAS_OFFER_TYPES,
} from 'constants/offerList'
import { HOTEL_SEARCH_TYPES, OFFER_TYPE_BED_BANK, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_VILLA } from 'constants/offer'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import * as Analytics from 'analytics/analytics'
import { impressionEventWithContext, productClickEventWithContext } from 'analytics/snowplow/events'
import getOfferListKey from 'lib/offer/offerListKey'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import useOfferListExtras from 'hooks/Offers/useOfferListExtras'
import useStreamSearchExpired from 'hooks/Offers/useStreamSearchExpired'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import RefreshSearchModal from 'components/SearchV2/Components/Mobile/RefreshSearchModal'
import ModalContext from 'contexts/ModalContext'
import useSearchEvent from 'hooks/Search/useSearchEvent'
import { addRecentSearch } from 'actions/SearchActions'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { EmptyObject } from 'lib/object/objectUtils'
import {
  mapGlobalSearchContextToSnowplowSearchEvent,
  mapOfferListFiltersToFilterContexts, mapOfferListFiltersToSortContext,
} from 'analytics/mapSnowplowSearchTracking'
import moment from 'moment/moment'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import useSortWasApplied from 'hooks/Search/useSortWasApplied'
import useFilterWasApplied from 'hooks/Search/useFilterWasApplied'
import useTrackingSearchEvent from 'hooks/Search/useTrackingSearchEvent'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'
import useSearchSession from '../../../../hooks/Search/useSearchSession'
import { getAccommodationSearchHeadMeta } from '../../HotelSearchPage/utils/getAccommodationSearchHeadMeta'
import SiteTakeoverSearchBanner from 'takeovers/utils/components/SiteTakeoverSearchBanner'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'

const ANALYTICS_HOTEL_SEARCH_TAG = 'HVS' // Homes and Villas Search

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: ${rem(32)};
  padding-bottom: ${rem(32)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
    padding-bottom: ${rem(24)};
  }

  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.default.eight};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 1fr);
      padding-top: ${rem(12)};
    }
  }
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

const StyledOfferList = styled(OfferList)`
  width: 100%;
`

const HeadingWrapper = styled.div`
  padding-left: ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding-left: 0;
  }
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux, RouteComponentProps<any> {}

function HomesAndVillasSearchPage({
  filters,
  match,
  searchResultMetadata,
  windowSearch,
  isFlexibleSearch,
  location,
  history,
}: Props) {
  const dispatch = useAppDispatch()
  const locationLabels = useLocationSearchLabels()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const isDesktop = useScreenSizeUp('largeDesktop')
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/homes-and-villas/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const pageSize = SEARCH_STREAMING_PAGE_SIZE
  const specificPlaces = useAppSelector(state =>
    filters.destinationId ? state.destination.specificPlaces[filters.destinationId] : undefined,
  )
  const destinationImageId = useAppSelector(state =>
    filters.destinationId ? state.destination.places[filters.destinationId]?.imageId : undefined,
  )

  const searchKey = getSearchKey(params)
  const { searchId, searchEvent: searchSessionSearchEvent } = useSearchEvent(searchKey)
  const searchSessionKey = createSearchSessionKey({ destinationId: params.destinationId, propertyId: params.propertyId, landmarkId: params.landmarkId })
  const { searchSessionId } = useSearchSession(searchSessionKey)
  const shouldShowBanner = useShouldShowBanner({ location: 'search', vertical: 'villas' })

  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[(params.landmarkId || params.destinationId)!] ?? EmptyObject,
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const showFilterPane = isDesktop

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const enabledFilters: EnabledFilters = useMemo(() => ({
    inclusions: true,
    amenities: true,
    customerRating: true,
    priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
    holidayTypes: true,
    locations: searchTargetLabel === ANYWHERE_NAME,
    offerTypes: config.ENABLE_VILLA_RENTALS,
    bedrooms: true,
    popularFilters: false,
    beds: true,
    bathrooms: true,
    luxPlusFeatures: luxPlusEnabled,
    agentHubFeatures: config.agentHub.isEnabled,
    chains: false,
  }), [luxPlusEnabled, searchTargetLabel])

  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams
    if (searchItem && (searchType === 'destination' || searchType === 'property')) {
      const dates = {
        checkIn: specificDates?.checkIn,
        checkOut: specificDates?.checkOut,
        flexibleMonths: flexibleSearch?.flexibleMonths,
        flexibleDuration: flexibleSearch?.flexibleNights,
        isAnytime: !(specificDates?.checkIn || specificDates?.checkOut || flexibleSearch?.flexibleMonths || flexibleSearch?.flexibleNights),
      }
      dispatch(addRecentSearch(searchItem, dates, rooms))
    }
  }, [searchParams, searchType, dispatch])

  useEffect(() => {
    if (searchParams?.searchItem?.searchType === 'destination') {
      dispatch(fetchSpecificPlaces(searchParams.searchItem.value))
    }
  }, [dispatch, searchParams.searchItem])

  useEffect(() => {
    if (destinationImageId || !filters.destinationId || filters.destinationId === ANYWHERE_PLACE_ID) {
      return
    }

    dispatch(fetchDestinationImageId(filters.destinationId))
  }, [dispatch, filters.destinationId, destinationImageId])

  const formRef = useRef<SearchFormRef>(null)

  const onBedbankSelectDatesPrompt = useCallback((offerId: string, offerType: App.OfferType, windowSearch: string) => {
    const paramsToSet = [{ paramName: 'offerType', paramValue: offerType }, { paramName: 'offerId', paramValue: offerId }]
    if (isMobileScreen) {
      toggleSearchVertical('homes-and-villas', { withDateSelect: true, search: setManySearchParamValues(windowSearch, paramsToSet) })
    } else {
      history.replace({
        search: setManySearchParamValues(windowSearch, paramsToSet),
        hash: window.location.hash,
      })
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [history, isMobileScreen, toggleSearchVertical])

  const globalHomesAndVillasSearchDispatchRef = useRef<React.Dispatch<GlobalSearchAction> | null>(null)

  const onAnyHotelSelectDatesPrompt = useCallback((offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    const { propertySearchItem, mobileSearchParams } = buildSelectDatesSearchParams(offer)

    if (globalHomesAndVillasSearchDispatchRef.current) {
      globalHomesAndVillasSearchDispatchRef.current({
        type: GlobalSearchStateActions.SET_SEARCH_ITEM,
        searchItem: propertySearchItem,
      })
    }

    if (isMobileScreen) {
      toggleSearchVertical('homes-and-villas', {
        withDateSelect: true,
        search: mobileSearchParams,
      })
    } else {
      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobileScreen, toggleSearchVertical])

  const queryParams = useQueryParams()

  const utmBp = useAppSelector(state => state.utm.bp)
  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !isFlexibleSearch
  const hideAnytimePrices = shouldHideAnytimePrices(isAnytimeSearch, utmBp)

  const { globalHomesAndVillasSearchDispatch, globalHomesAndVillasSearchState } = useGlobalHomesAndVillasSearch({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
    onBedbankSelectDatesPrompt,
    onAnyHotelSelectDatesPrompt,
    hideAnytimePrices,
  }, queryParams)

  useEffect(() => {
    globalHomesAndVillasSearchDispatchRef.current = globalHomesAndVillasSearchDispatch
  }, [globalHomesAndVillasSearchDispatch])

  useEffect(() => {
    globalHomesAndVillasSearchDispatch({
      type: GlobalSearchStateActions.SET_HIDE_ANYTIME_PRICES_ENABLED,
      hideAnytimePrices,
    })
  }, [hideAnytimePrices, globalHomesAndVillasSearchDispatch])

  useEffect(() => {
    globalHomesAndVillasSearchDispatch({
      type: GlobalSearchStateActions.SET_OFFER_DISTANCE_FROM_SEARCH_TARGET,
      offerDistanceFromSearchTarget,
    })
  }, [offerDistanceFromSearchTarget, globalHomesAndVillasSearchDispatch])

  useEffect(() => {
    globalHomesAndVillasSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL,
      searchTargetLabel,
    })
  }, [searchTargetLabel, globalHomesAndVillasSearchDispatch])

  useIdleTimeReporter(IdleTimeSource.HotelSearchPage)

  const invalidSearchParams = useMemo(() =>
    !validateDestinationSearchParams(params) &&
    !validateLandmarkSearchParams(params) &&
    !validatePropertySearchParams(params) &&
    !validateBoundsSearchParams(params) &&
    !validateNaturalLanguageSearchParams(params),
  [params])

  useEffect(() => {
    const onSearchUrlPath = match.url === location.pathname
    if (invalidSearchParams && onSearchUrlPath) {
      dispatch(replaceWithRegion('/homes-and-villas'))
    }
  }, [dispatch, invalidSearchParams, location.pathname, match])

  const listFilters = useMemo((): App.OfferListFilters => {
    const { chains: _chains, ...filtersWithoutChains } = filters
    const offerTypes: Array<App.OfferListOfferType> = filters.propertyId
      ? [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK, OFFER_TYPE_VILLA]
      : HOMES_AND_VILLAS_OFFER_TYPES

    return {
      ...filtersWithoutChains,
      ...HOMES_AND_VILLAS_FILTERS,
      holidayTypes: filters.holidayTypes,
      distanceEq: searchType === 'destination' ? 0 : undefined,
      scheduleVisibilities: isSpoofed ? ['listVisible', 'spoofVisible'] : ['listVisible'],
      include: ['nearby'],
      isStream: true,
      offerTypes,
      holidayTypesScoped: filters.propertyId ? [] : HOMES_AND_VILLAS_FILTERS.holidayTypesScoped,
    }
  }, [filters, searchType, isSpoofed])

  const streamedExtras = useOfferListExtras(listFilters)
  const searchExpired = useStreamSearchExpired(listFilters)
  const showModal = useContext(ModalContext)

  useEffect(() => {
    if (searchExpired) {
      Analytics.trackClientEvent({
        subject: 'expired',
        action: 'show',
        category: 'search',
        type: 'nonInteraction',
      })

      showModal(<RefreshSearchModal />)
    }
  }, [searchExpired, showModal])

  const { globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
    onBedbankSelectDatesPrompt,
    onAnyHotelSelectDatesPrompt,
    eventAnalytics: { searchId },
  }, queryParams)

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      flexibleNights: listFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: listFilters.flexibleMonths,
      durationRange: listFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkinDate: listFilters.checkIn ? moment(listFilters.checkIn) : undefined,
      checkoutDate: listFilters.checkOut ? moment(listFilters.checkOut) : undefined,
      searchItem: searchParams?.searchItem,
      eventAnalytics: { searchId },
    })
  }, [globalHotelsSearchState, listFilters, searchParams?.searchItem, searchId])

  const filterContexts = useMemo(() => {
    return mapOfferListFiltersToFilterContexts({ offerListFilters: listFilters })
  }, [listFilters])

  const sortContext = useMemo(() => {
    return mapOfferListFiltersToSortContext({ offerListFilters: listFilters })
  }, [listFilters])

  const sortWasApplied = useSortWasApplied(listFilters)

  const filterWasApplied = useFilterWasApplied(listFilters)

  const trackingSearchEvent = useTrackingSearchEvent({
    searchSessionSearchEvent, sortWasApplied, filterWasApplied,
  })

  const trackingOptions = useMemo<TrackingOptions>(() => (
    { searchEvent: trackingSearchEvent, searchContext, filterContexts, sortContext, searchSessionId }
  ), [trackingSearchEvent, searchContext, filterContexts, sortContext, searchSessionId])

  const minRequiredOffers = useMemo(() => {
    return (parseInt(params.pages ?? '1') * pageSize) || undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.pages])

  const hotelOfferList = useOfferList(listFilters, {
    minRequiredOffers,
  }, trackingOptions)

  const searchListKey = hotelOfferList.key

  const deprecatedSearchSessionId = useAppSelector(state => state.analytics.search.sessions[searchListKey])

  const listDepth = useRef<number>(0)
  useEffect(() => {
    return () => {
      if (hotelOfferList.offerIds.length) {
        // On a new search, finalise tracking the depth reached on the old search
        addGTMEvent(searchResultImpressionCount(hotelOfferList.offerIds.length, listDepth.current))
        // event sent, now reset the depth we're at. They probably applied a filter and will be at the top again
        listDepth.current = 0
      }
    }
    // eslint-disable-next-line
  }, [searchListKey])

  useEffect(() => {
    if (listFilters) {
      addGTMEvent(searchOfferListFilters(listFilters))
    }
  }, [listFilters])

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Offer List'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, price, memberPrice, duration, availability } = dispatchAction
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(deprecatedSearchSessionId, offer.id))

      Analytics.trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext },
        ),
      )
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, roomRateId } = dispatchAction
      listDepth.current = Math.max(position + 1, listDepth.current)

      Analytics.trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, searchContext, roomRateId },
        ),
      )
    }
  }, [searchContext, deprecatedSearchSessionId, tracking.listId, tracking.listName])

  const onPageChange = useCallback((page: number) => {
    dispatch(replace({
      search: setSearchParamValue(windowSearch, 'pages', page),
      hash: window.location.hash,
    }))
    // eslint-disable-next-line
  }, [windowSearch])

  const sortOptions = useMemo(() => {
    let options = searchType === 'landmark' ? landmarkSearchSortByTypes : homesAndVillasSortByTypes

    options = [...options, SORT_OPTION_CUSTOMER_RATING]

    return options
  }, [searchType])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: window.location.hash,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const mainListTotal = hotelOfferList.offerCount ?? 0
  const totalOffersForMeta = mainListTotal || hotelOfferList.offerIds?.length || 0

  const searchHeadMeta = useMemo(() => getAccommodationSearchHeadMeta({
    searchItem: searchParams.searchItem,
    destinationId: filters.destinationId,
    destinationName: filters.destinationName,
    totalCount: totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  }), [
    searchParams.searchItem,
    filters.destinationId,
    filters.destinationName,
    totalOffersForMeta,
    specificPlaces,
    destinationImageId,
  ])

  if (invalidSearchParams) {
    return null
  }

  const mainListFetching = hotelOfferList.fetching
  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: 'Map view',
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  const sortByPrice = listFilters.sortBy === 'price.asc' || listFilters.sortBy === 'price.desc'
  const showLoadingAnimation = mainListFetching && sortByPrice && !isAnytimeSearch

  const positionOfFirstSeparator = streamedExtras.length > 0 ? streamedExtras[0].position : undefined
  const noExactMatchesForPropertySearch = !mainListFetching && positionOfFirstSeparator === 0
  const isZSR = searchType === 'destination' && mainListTotal === 0 && !mainListFetching

  const extras: Array<OfferListV2Item> = [{
    id: 'no-results',
    position: 'start',
    element: <HotelSearchNoSearchResultSection />,
    show: noExactMatchesForPropertySearch || isZSR,
  }, {
    id: 'heading',
    position: 'start',
    element: <OfferCountAndSort
      mainListTotal={mainListTotal}
      sortOptions={sortOptions}
      mapViewUrl={mapViewURL}
      isFlexible={isFlexibleSearch}
      filters={listFilters}
      windowSearch={windowSearch}
      shouldShowFilters={!showFilterPane}
      showHolidayTypes={searchTargetLabel !== ANYWHERE_NAME}
      fetching={mainListFetching}
      enabledFilters={enabledFilters}
      showOfferCount={searchType === 'destination'}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
    />,
  },
  {
    id: 'loading-animation',
    position: 'start',
    element: <LoadingAnimationBox variant="hotels" />,
    show: showLoadingAnimation,
  },
  ...streamedExtras.map(extra => ({
    id: extra.id,
    position: extra.position,
    element: (
      <HeadingWrapper>
        <Heading variant="heading4">{extra.heading}</Heading>
      </HeadingWrapper>
    ),
    show: 'hasOffers' as const,
  })),
  ]

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <GlobalSearchContextProvider
        state={globalHomesAndVillasSearchState}
        dispatch={globalHomesAndVillasSearchDispatch}
      >
        <HeadData
          noIndex
          title={searchHeadMeta.title}
          description={searchHeadMeta.description}
          imageId={searchHeadMeta.imageId}
          imageUrl={searchHeadMeta.imageUrl}
          imageAlt={searchHeadMeta.imageAlt}
        />
        {shouldShowBanner && <SiteTakeoverSearchBanner vertical="villas" />}
        <HotelSearchHeader
          filters={filters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
          searchVertical="homes-and-villas"
          baseSearchPath="/search/homes-and-villas"
          baseSearchMapPath="/search/homes-and-villas/map"
          disableAnywhereFilterTabs
          locationSearchPlaceholder={locationLabels.homesAndVillasPlaceholderLabel}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
        />
        <ErrorBoundary fallback={null}>
          <PromptCardSelector tripReengagement />
        </ErrorBoundary>
        <StyledLayoutContainer size={showFilterPane ? 'xl' : 'lg'} className={clsx({ filterPane: showFilterPane })}>
          <StyledVerticalSpacer gap={16}>
            <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
              <StyledOfferList
                filters={listFilters}
                interstitials={extras}
                onPageChange={onPageChange}
                searchId={searchId}
                pageSize={pageSize}
                tileStyle="search"
                eagerLoadFirstImage
                defaultPage={params.pages ? parseInt(params.pages) : 1}
              />
            </OfferListEventsProvider>
          </StyledVerticalSpacer>
          {showFilterPane && <StyledHotelSearchPageFilterPane
            filters={listFilters}
            mapViewURL={mapViewURL}
            search={windowSearch}
            enabledFilters={enabledFilters}
            onFilterChange={handleFilterChange}
            showRoomsAndAmenitiesAtTop
          />}
        </StyledLayoutContainer>
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

export default withRouter(connector(HomesAndVillasSearchPage))
